import React from 'react';
import styled, { css } from 'styled-components';
import { isNil } from 'ramda';
import { SortDirection } from '~/graphql/types';
import { TableHeaderCellType } from '~/components/organism/Table/types';
import { headerProps } from '../../../../utils/propGetters';
import ColumnResizer from '../../../ColumnResizer';
import getNextSortDirection from '../../../../utils/getNextSortDirection';
import SortToggle from '~/components/molecule/SortToggle';
import TEST_ID from './index.testid';

type Props = {
  column: TableHeaderCellType<any>;
  isLastColumn: boolean;
  deselectAllItems?: () => void;
};

const HeaderCell: React.FCC<Props> = ({
  column,
  isLastColumn,
  deselectAllItems,
  ...rest
}) => {
  const styledThProps = column.getHeaderProps({
    ...headerProps,
  });

  const sortByToggleProps = {
    ...column.getSortByToggleProps({
      title: '',
    }),
  };

  const handleSortClick = (...args) => {
    if (deselectAllItems) deselectAllItems();

    if (!isNil(sortByToggleProps.onClick)) {
      sortByToggleProps.onClick(...args);
    }

    const onSort = column.onSort;
    if (onSort) {
      onSort(getNextSortDirection(column.isSorted, column.isSortedDesc));
    }
  };

  const sortable = !column.disableSortBy;
  const resizable = column.canResize && !isLastColumn;
  const sortDirection = column.isSorted
    ? column.isSortedDesc
      ? SortDirection.Desc
      : SortDirection.Asc
    : null;

  return (
    <Container
      {...styledThProps}
      {...rest}
      data-testid={column.dataTestId}
      data-sortable={sortable}
      data-sorted={sortDirection || false}
      data-resizable={resizable}
      key={styledThProps.key}
    >
      <ContentContainer>
        <OverflowHandlerContainer
          {...sortByToggleProps}
          onClick={handleSortClick}
          data-testid={
            column.dataTestId && TEST_ID.LABEL_CONTAINER(column.dataTestId)
          }
        >
          {column.render('Header')}
        </OverflowHandlerContainer>
        {sortable && (
          <StyledSortToggle
            {...sortByToggleProps}
            data-testid={
              column.dataTestId && TEST_ID.SORT_ICON(column.dataTestId)
            }
            onClick={handleSortClick}
            direction={sortDirection}
          />
        )}
        {resizable && <ColumnResizer column={column} />}
      </ContentContainer>
    </Container>
  );
};

const ContentContainer = styled.div<{}>`
  display: flex;
  align-items: center;
  height: 100%;
`;

const Container = styled.div<{}>(
  ({ theme }) => css`
    font-weight: ${theme.fw('semiBold')};
    padding-left: ${theme.space('xxs')};
  `,
);

const OverflowHandlerContainer = styled.div<{}>`
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledSortToggle = styled(SortToggle)<{}>`
  ${({ theme }) => css`
    padding-left: ${theme.space('xxs')};
  `}
`;

export default HeaderCell;
